import { Capacitor } from "@capacitor/core";
import { Link } from "@tanstack/react-router";
import {
  Check,
  CheckCircle,
  ChefHat,
  ChevronDown,
  Download,
  Globe,
  Menu,
  Package,
  RefreshCw,
  ShoppingCart,
  Smartphone,
  Users,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useInstallPrompt } from "../hooks/useInstallPrompt";
import type { HouseholdInvitation } from "../services/invitationService";
import AuthForm from "./AuthForm";
import ThemeToggle from "./ThemeToggle";

// ---------------------------------------------------------------------------
// useReveal — triggers .is-visible once an element scrolls into view
// ---------------------------------------------------------------------------

function useReveal() {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

// ---------------------------------------------------------------------------
// Static data
// ---------------------------------------------------------------------------

const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.hillardworks.pantry";

const NAV_LINKS = [
  { href: "#features", label: "Features" },
  { href: "#how-it-works", label: "How It Works" },
  { href: "#pricing", label: "Pricing" },
  { href: "#faq", label: "FAQ" },
];

const STATS = [
  { value: "$9.99", label: "Web license" },
  { value: "$14.99", label: "Native app license" },
  { value: "5", label: "Household invites" },
  { value: "0", label: "Subscriptions, ever" },
];

const FEATURES = [
  {
    icon: Package,
    title: "Smart Inventory",
    description:
      "Track and manage everything in your pantry, fridge, and freezer with your whole household.",
    color: "blue",
  },
  {
    icon: ShoppingCart,
    title: "Shared Shopping List",
    description:
      "Collaborate in real-time with others. Everyone gets updates instantly.",
    color: "green",
  },
  {
    icon: ChefHat,
    title: "Recipe Discovery",
    description:
      "Find recipes based on ingredients you already have. Save them to your collection of favorites.",
    color: "purple",
  },
  {
    icon: Users,
    title: "Household Sharing",
    description:
      "Invite up to 5 household members for free. One shared pantry, everyone in sync.",
    color: "orange",
  },
  {
    icon: RefreshCw,
    title: "Real-time Sync",
    description:
      "Changes appear instantly across all devices. No refresh needed.",
    color: "cyan",
  },
  {
    icon: Smartphone,
    title: "Mobile First",
    description:
      "Native Android app for a fast, offline-ready experience wherever you are.",
    color: "rose",
  },
];

const HOW_IT_WORKS = [
  {
    step: "1",
    title: "Create your account",
    description:
      "Sign up at the top of this page and confirm your email address.",
  },
  {
    step: "2",
    title: "Purchase a license",
    description:
      "Choose the Web License for $9.99 or the Native App License for $14.99.",
  },
  {
    step: "3",
    title: "Create your household",
    description:
      "Login and create a household. Your inventory and shopping list exists here.",
  },
  {
    step: "4",
    title: "Invite your family",
    description:
      "Share an invite link with up to 5 members. Everyone gets real-time access.",
  },
];

const FAQS = [
  {
    q: "Is Pantry really a one-time purchase?",
    a: "Yes. There are two options: a $9.99 Web License (browser & PWA) purchased here, and a $14.99 Native App License via Google Play that includes everything in the web license. No subscriptions, no recurring fees.",
  },
  {
    q: "How many people can share a household?",
    a: "A total of 1 admin plus 5 other members can share a single household.",
  },
  {
    q: "Can I be a member of multiple households?",
    a: "Yes. You can be invited to multiple households, but can only be the admin of one household (each admin account is tied to a unique email address). There is a household switcher in the interface that allows you to seamlessly switch between them without signing out.",
  },
  {
    q: "Does it work offline?",
    a: "Yes, and it syncs automatically when you reconnect. The app utilizes service workers to cache data for offline use.",
  },
  {
    q: "Can I use it on iOS?",
    a: "Right now Pantry is available on Android and the web. iOS support is on the roadmap, and you can always install the progressive web app (PWA) from your favorite mobile web browser.",
  },
  {
    q: "How do I get started?",
    a: "Sign up for a new account above to access the web app and then purchase a license. Download from Google Play for the full Android experience, where you can sign up directly in the app and immediately gain access to the dashboard after you're logged in.",
  },
];

const iconColorMap: Record<string, string> = {
  blue: "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400",
  green: "bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-400",
  purple:
    "bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-400",
  orange:
    "bg-orange-100 text-orange-600 dark:bg-orange-900/40 dark:text-orange-400",
  cyan: "bg-cyan-100 text-cyan-600 dark:bg-cyan-900/40 dark:text-cyan-400",
  rose: "bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-400",
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function GooglePlayButton({ className = "" }: { className?: string }) {
  return (
    <a
      href={GOOGLE_PLAY_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-lg hover:bg-blue-700 ${className}`}
    >
      <Smartphone className="h-4 w-4" />
      Get on Google Play
    </a>
  );
}

// ---------------------------------------------------------------------------
// Section wrappers
// ---------------------------------------------------------------------------

function RevealSection({
  id,
  className = "",
  children,
  "aria-label": ariaLabel,
}: {
  id?: string;
  className?: string;
  children: React.ReactNode;
  "aria-label"?: string;
}) {
  const { ref, visible } = useReveal();
  return (
    <section
      id={id}
      ref={ref}
      aria-label={ariaLabel}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`}
    >
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// AndroidLandingPage — minimal sign-in focused layout for the native Android app
// ---------------------------------------------------------------------------

function AndroidLandingPage({
  pendingInvitation,
}: {
  pendingInvitation?: HouseholdInvitation | null;
}) {
  return (
    <div className="pt-safe flex min-h-screen flex-col overflow-x-clip bg-white text-gray-900 dark:bg-gray-950 dark:text-white">
      {/* Top gradient orb blobs */}
      <div className="relative inset-0 w-full">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-40 left-1/4 h-150 w-150 -translate-x-1/2 rounded-full bg-blue-400/20 blur-3xl dark:bg-blue-600/15"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-20 right-0 h-125 w-125 translate-x-1/3 rounded-full bg-indigo-400/20 blur-3xl dark:bg-indigo-600/15"
        />
      </div>

      {/* Bottom gradient orb blobs */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-0 h-100 w-100 -translate-x-1/4 rounded-full bg-blue-400/15 blur-3xl dark:bg-blue-600/10"
      />

      {/* Header */}
      <header className="relative flex items-center justify-between px-4 py-4">
        <div className="flex items-center gap-3">
          <img
            src="/favicon-browser.svg"
            alt="Pantry logo"
            className="h-8 w-8"
          />
          <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            Pantry
          </span>
        </div>
        <ThemeToggle />
      </header>

      {/* Main content */}
      <main className="relative flex flex-1 flex-col items-center justify-center px-4 py-8">
        <h1 className="mb-5 text-4xl leading-tight font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl dark:text-white">
          <span className="bg-linear-to-r from-blue-600 via-cyan-500 to-green-500 bg-clip-text text-transparent dark:from-blue-400 dark:via-cyan-400 dark:to-green-400">
            Welcome!
          </span>
        </h1>
        <p className="mb-8 max-w-xl px-2 text-center text-lg leading-relaxed text-gray-600 dark:text-gray-400">
          Sign in to access your dashboard. If you don't have an account yet,
          sign up and confirm your email address.
        </p>
        <p className="mb-4 text-center text-xs font-semibold tracking-widest text-gray-500 uppercase dark:text-gray-500">
          Sign in or create an account
        </p>
        <div className="w-full max-w-sm">
          <AuthForm pendingInvitation={pendingInvitation} />
        </div>
      </main>

      {/* Footer */}
      <footer className="pb-safe relative mb-6 px-4 py-6 text-center">
        <Link
          to="/privacy"
          className="text-sm text-gray-400 hover:text-gray-600 dark:text-gray-600 dark:hover:text-gray-400"
        >
          Privacy Policy
        </Link>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WebLandingPage — full marketing page for web users
// ---------------------------------------------------------------------------

function WebLandingPage({
  pendingInvitation,
}: {
  pendingInvitation?: HouseholdInvitation | null;
}) {
  const { canInstall, install } = useInstallPrompt();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [faqOpen, setFaqOpen] = useState<number | null>(null);
  const [authView, setAuthView] = useState<"signin" | "signup">("signin");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen overflow-x-clip bg-white text-gray-900 dark:bg-gray-950 dark:text-white">
      {/* Skip links */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-100 focus:rounded-lg focus:bg-blue-600 focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:outline-none"
      >
        Skip to main content
      </a>
      <a
        href="#features"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-48 focus:z-100 focus:rounded-lg focus:bg-blue-600 focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:outline-none"
      >
        Skip to features
      </a>
      <a
        href="#pricing"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-80 focus:z-100 focus:rounded-lg focus:bg-blue-600 focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:outline-none"
      >
        Skip to pricing
      </a>

      {/* Top gradient orb blobs */}
      <div className="relative inset-0 w-full">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-40 left-1/4 h-150 w-150 -translate-x-1/2 rounded-full bg-blue-400/20 blur-3xl dark:bg-blue-600/15"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-20 right-0 h-125 w-125 translate-x-1/3 rounded-full bg-indigo-400/20 blur-3xl dark:bg-indigo-600/15"
        />
      </div>

      {/* ===NAVBAR=== */}
      <header
        className={`pt-safe sticky inset-x-0 top-0 z-50 ${
          scrolled
            ? "bg-white/80 shadow-sm backdrop-blur-md dark:bg-gray-950/80"
            : "bg-transparent"
        }`}
      >
        <nav
          aria-label="Main"
          className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8"
        >
          {/* Logo */}
          <a
            href="#top"
            className="flex items-center gap-3 font-bold text-gray-900 dark:text-white"
          >
            <img
              src="/favicon-browser.svg"
              alt="Pantry logo"
              className="h-8 w-8"
            />
            <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">
              Pantry
            </span>
          </a>

          {/* Desktop nav links */}
          <div className="hidden items-center gap-6 md:flex">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-sm font-medium text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
              >
                {l.label}
              </a>
            ))}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            <ThemeToggle />
            {canInstall && (
              <button
                type="button"
                onClick={install}
                className="rounded-lg bg-gray-100 p-2 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
                aria-label="Install app"
                title="Install app"
              >
                <Download className="h-5 w-5" aria-hidden="true" />
              </button>
            )}
            {/* Mobile hamburger */}
            <button
              type="button"
              className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 md:hidden dark:text-gray-400 dark:hover:bg-gray-800"
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
              onClick={() => setMobileMenuOpen((v) => !v)}
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Menu className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
          </div>
        </nav>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div
            id="mobile-menu"
            className="fixed w-full border-t border-gray-200 bg-white px-4 py-4 md:hidden dark:border-gray-800 dark:bg-gray-950"
          >
            <div className="flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {l.label}
                </a>
              ))}
              <a
                href={GOOGLE_PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white"
              >
                Get the App
              </a>
            </div>
          </div>
        )}
      </header>

      {/* ===HERO=== */}
      <section
        id="top"
        aria-label="Hero"
        className="relative overflow-hidden pt-8 sm:pt-12"
      >
        {/* Bottom gradient orb blob */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-0 left-0 h-100 w-100 -translate-x-1/4 rounded-full bg-blue-400/15 blur-3xl dark:bg-blue-600/10"
        />

        <div className="relative mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
          {/* Hero: text + auth form */}
          <div className="flex flex-col items-center gap-10 lg:flex-row lg:items-center lg:justify-between lg:gap-16">
            {/* Left — headline / CTAs */}
            <div className="animate-fade-up w-full max-w-xl pt-6 text-center lg:shrink-0 lg:text-left">
              {/* Badge */}
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-green-200 bg-green-50 px-4 py-1.5 text-sm font-medium text-green-700 dark:border-green-800 dark:bg-green-950/50 dark:text-green-400">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
                </span>
                One-time purchase — from $9.99
              </div>

              <h1 className="mb-5 text-4xl leading-tight font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl dark:text-white">
                Keep your kitchen{" "}
                <span className="bg-linear-to-r from-blue-600 via-cyan-500 to-green-500 bg-clip-text text-transparent dark:from-blue-400 dark:via-cyan-400 dark:to-green-400">
                  stocked, organized,
                </span>{" "}
                and ready to cook
              </h1>

              <p className="mb-8 max-w-xl text-lg leading-relaxed text-gray-600 dark:text-gray-400">
                Pantry is the household kitchen app that keeps everyone in sync.
                Shared inventory, collaborative shopping list, and recipe ideas
                from what you already have.
              </p>

              <div className="flex flex-wrap justify-center gap-3 lg:justify-start">
                <GooglePlayButton />
                <a
                  href="#how-it-works"
                  className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  See how it works
                </a>
              </div>

              {/* Feature bullets */}
              <ul className="mx-auto mt-8 flex w-fit flex-col gap-2 text-left text-sm text-gray-600 lg:w-auto dark:text-gray-400">
                {[
                  "Real-time sync across all devices",
                  "Invite up to 5 household members for free",
                  "Reduce food waste and save money",
                ].map((t) => (
                  <li key={t} className="flex items-start justify-start gap-2">
                    <CheckCircle className="h-4 w-4 shrink-0 text-green-500" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right — AuthForm card */}
            <div
              id="main-content"
              className="animate-fade-up w-full max-w-md shrink-0 sm:shrink lg:w-105"
              style={{ animationDelay: "120ms" }}
            >
              <p className="mb-4 text-center text-xs font-semibold tracking-widest text-gray-500 uppercase dark:text-gray-500">
                Sign in or create an account
              </p>
              <AuthForm
                pendingInvitation={pendingInvitation}
                initialView={authView}
                key={authView}
              />
            </div>
          </div>

          <div
            className="animate-fade-up mx-auto flex w-full max-w-7xl flex-col items-center justify-center overflow-hidden"
            style={{ animationDelay: "240ms" }}
          >
            {/* Perspective Container */}
            <div
              role="img"
              aria-label="Pantry running on desktop, tablet, and mobile devices"
              className="relative flex w-full items-center justify-center py-12 sm:py-16 md:py-20"
              style={{ perspective: "1600px" }}
            >
              {/* 1. Desktop Monitor (Left / Back Layer) */}
              <div className="relative z-0 aspect-16/10 w-[58%] rotate-x-12 rotate-y-24 rotate-z-[-4deg] transform rounded-lg border-2 border-slate-300 bg-slate-200 shadow-xl transition-all transform-3d md:min-w-90 md:rounded-xl md:border-4 lg:rounded-2xl dark:border-slate-600 dark:bg-slate-700">
                {/* Screen Content Frame */}
                <div className="flex h-full w-full flex-col p-1 md:p-1.5">
                  {/* Top Browser Bar */}
                  <div className="flex items-center gap-1.5 border-b border-slate-300 px-1 pb-1 md:pb-1.5 dark:border-slate-600">
                    <span className="h-1.5 w-1.5 rounded-full bg-rose-500/80 md:h-2 md:w-2"></span>
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500/80 md:h-2 md:w-2"></span>
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500/80 md:h-2 md:w-2"></span>
                  </div>
                  {/* App Screenshot Wrapper */}
                  <div className="relative mt-1 flex-1 overflow-hidden rounded-md bg-white md:mt-2 md:rounded-lg dark:bg-slate-950">
                    {/* Light Mode Screenshot */}
                    <img
                      src="./screenshots/pantry-dashboard-desktop.webp"
                      alt=""
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 h-full w-full object-cover object-top opacity-100 transition-opacity duration-300 select-none dark:opacity-0"
                    />
                    {/* Dark Mode Screenshot */}
                    <img
                      src="./screenshots/pantry-dashboard-desktop-dark.webp"
                      alt=""
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 h-full w-full object-cover object-top opacity-0 transition-opacity duration-300 select-none dark:opacity-100"
                    />
                  </div>
                </div>
              </div>

              {/* 2. Tablet (Middle Layer) */}
              <div className="relative z-10 ml-[-16%] aspect-3/4 w-[32%] rotate-x-12 rotate-y-24 rotate-z-[-4deg] transform rounded-lg border-2 border-slate-300 bg-slate-200 shadow-xl transition-all transform-3d md:ml-[-15%] md:min-w-50 md:rounded-xl md:border-4 lg:rounded-2xl dark:border-slate-600 dark:bg-slate-700">
                {/* Screen Content Frame */}
                <div className="flex h-full w-full flex-col p-1 md:p-1.5">
                  {/* Tablet Camera / Notch Indicator */}
                  <div className="flex w-full justify-center pb-1 md:pb-2">
                    <span className="h-1 w-1 rounded-full bg-slate-400 md:h-1.5 md:w-1.5 dark:bg-slate-600"></span>
                  </div>
                  {/* App Screenshot Wrapper */}
                  <div className="relative flex-1 overflow-hidden rounded-md bg-white md:rounded-lg dark:bg-slate-950">
                    {/* Light Mode Screenshot */}
                    <img
                      src="./screenshots/pantry-dashboard-tablet.webp"
                      alt=""
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 h-full w-full object-cover object-top opacity-100 transition-opacity duration-300 select-none dark:opacity-0"
                    />
                    {/* Dark Mode Screenshot */}
                    <img
                      src="./screenshots/pantry-dashboard-tablet-dark.webp"
                      alt=""
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 h-full w-full object-cover object-top opacity-0 transition-opacity duration-300 select-none dark:opacity-100"
                    />
                  </div>
                </div>
              </div>

              {/* 3. Mobile Phone (Right / Front Layer) */}
              <div className="relative z-20 ml-[-8%] aspect-9/19 w-[15.5%] rotate-x-12 rotate-y-24 rotate-z-[-4deg] transform rounded-lg border-2 border-slate-300 bg-slate-200 shadow-xl transition-all transform-3d md:ml-[-7.5%] md:min-w-27.5 md:rounded-xl md:border-[5px] lg:rounded-2xl dark:border-slate-600 dark:bg-slate-700">
                {/* Screen Content Frame */}
                <div className="flex h-full w-full flex-col justify-between p-1 md:p-1.5">
                  {/* Refined, Proportional Dynamic Island */}
                  <div className="flex w-full justify-center pb-1 md:pb-1.5">
                    <span className="aspect-[3.2/1] w-[24%] max-w-10.5 rounded-full bg-slate-400 dark:bg-slate-600"></span>
                  </div>
                  {/* App Screenshot Wrapper */}
                  <div className="relative flex-1 overflow-hidden rounded-md bg-white md:rounded-lg dark:bg-slate-950">
                    {/* Light Mode Screenshot */}
                    <img
                      src="./screenshots/pantry-dashboard-mobile.webp"
                      alt=""
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 h-full w-full object-cover object-top opacity-100 transition-opacity duration-300 select-none dark:opacity-0"
                    />
                    {/* Dark Mode Screenshot */}
                    <img
                      src="./screenshots/pantry-dashboard-mobile-dark.webp"
                      alt=""
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 h-full w-full object-cover object-top opacity-0 transition-opacity duration-300 select-none dark:opacity-100"
                    />
                  </div>
                  {/* Bottom Nav Bar Indicator */}
                  <div className="mx-auto mt-1 h-0.5 w-1/3 rounded-full bg-slate-400 md:mt-2 md:h-1 md:w-1/2 dark:bg-slate-600"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===STATS=== */}
      <RevealSection
        aria-label="Key stats"
        className="border-y border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900"
      >
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-3xl font-extrabold text-blue-600 sm:text-4xl dark:text-blue-400">
                  {s.value}
                </p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </RevealSection>

      {/* ===FEATURES=== */}
      <RevealSection
        id="features"
        aria-label="Features"
        className="bg-white py-20 dark:bg-gray-950"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <p className="mb-2 text-sm font-semibold tracking-widest text-blue-600 uppercase dark:text-blue-400">
              Features
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl dark:text-white">
              Take control of your kitchen
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-gray-500 dark:text-gray-400">
              From tracking what you have to discovering what to cook, Pantry
              covers the full kitchen workflow.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              const colorCls = iconColorMap[f.color];
              return (
                <div
                  key={f.title}
                  className="group rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-md dark:border-gray-800 dark:bg-gray-900"
                >
                  <span
                    className={`mb-4 flex h-11 w-11 items-center justify-center rounded-xl ${colorCls}`}
                  >
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <h3 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    {f.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                    {f.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </RevealSection>

      {/* ===HOW IT WORKS=== */}
      <RevealSection
        id="how-it-works"
        aria-label="How it works"
        className="bg-gray-50 py-20 dark:bg-gray-900"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <p className="mb-2 text-sm font-semibold tracking-widest text-blue-600 uppercase dark:text-blue-400">
              How It Works
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl dark:text-white">
              Up and running in minutes
            </h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {HOW_IT_WORKS.map((s) => (
              <div key={s.step} className="p-2">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-linear-to-br from-blue-500 to-cyan-500 text-xs font-black text-white shadow">
                    {s.step}
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {s.title}
                  </h3>
                </div>
                <p className="text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                  {s.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </RevealSection>

      {/* ===PRICING=== */}
      <RevealSection
        id="pricing"
        aria-label="Pricing"
        className="bg-white py-20 dark:bg-gray-950"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <p className="mb-2 text-sm font-semibold tracking-widest text-blue-600 uppercase dark:text-blue-400">
              Pricing
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl dark:text-white">
              Simple, honest pricing
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-gray-500 dark:text-gray-400">
              Two options, one purchase each. No subscriptions.
            </p>
          </div>

          <div className="mx-auto grid max-w-4xl gap-8 md:grid-cols-2">
            {/* Web License */}
            <div className="flex flex-col rounded-2xl border-2 border-gray-200 bg-white p-8 shadow-lg dark:border-gray-700 dark:bg-gray-900">
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/40">
                  <Globe
                    className="h-6 w-6 text-blue-600 dark:text-blue-400"
                    aria-hidden="true"
                  />
                </span>
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white">
                    Web License
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Browser &amp; PWA
                  </p>
                </div>
              </div>
              <div className="mb-6 flex items-baseline gap-1">
                <span className="text-5xl font-extrabold text-gray-900 dark:text-white">
                  $9.99
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  one-time
                </span>
              </div>
              <ul className="mb-8 flex-1 space-y-3">
                {[
                  { text: "Works in any browser" },
                  { text: "Install as a Progressive Web App (PWA)" },
                  { text: "Create & manage your household" },
                  { text: "Invite up to 5 members (free for them)" },
                  { text: "Real-time sync across devices" },
                  { text: "Recipe search by ingredients" },
                ].map(({ text }) => (
                  <li key={text} className="flex items-start gap-2.5 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                    <span className="text-gray-700 dark:text-gray-300">
                      {text}
                    </span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => {
                  setAuthView("signup");
                  document
                    .getElementById("top")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-700"
              >
                <Globe className="h-4 w-4" aria-hidden="true" />
                Sign up &amp; Purchase Web License
              </button>
            </div>

            {/* Native App License */}
            <div className="relative flex flex-col rounded-2xl border-2 border-blue-500 bg-blue-50 p-8 shadow-xl dark:border-blue-600 dark:bg-blue-900/20">
              <div className="absolute -top-3.5 left-5 rounded-full bg-blue-600 px-3 py-1 text-xs font-bold text-white">
                BEST VALUE
              </div>
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/40">
                  <Smartphone
                    className="h-6 w-6 text-blue-600 dark:text-blue-400"
                    aria-hidden="true"
                  />
                </span>
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white">
                    Native App License
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Google Play Store
                  </p>
                </div>
              </div>
              <div className="mb-6 flex items-baseline gap-1">
                <span className="text-5xl font-extrabold text-gray-900 dark:text-white">
                  $14.99
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  one-time
                </span>
              </div>
              <ul className="mb-8 flex-1 space-y-3">
                {[
                  {
                    text: "Everything in Web License",
                    highlight: false,
                  },
                  { text: "Native Android app", highlight: false },
                  { text: "Better offline support", highlight: false },
                  { text: "Faster performance", highlight: false },
                  {
                    text: "Unlocks web app usage",
                    highlight: true,
                  },
                ].map(({ text, highlight }) => (
                  <li key={text} className="flex items-start gap-2.5 text-sm">
                    <Check
                      className={`mt-0.5 h-4 w-4 shrink-0 ${highlight ? "text-blue-500" : "text-green-500"}`}
                    />
                    <span
                      className={
                        highlight
                          ? "font-semibold text-blue-600 dark:text-blue-400"
                          : "text-gray-700 dark:text-gray-300"
                      }
                    >
                      {text}
                    </span>
                  </li>
                ))}
              </ul>
              <GooglePlayButton className="w-full justify-center" />
            </div>
          </div>

          <p className="mt-10 text-center text-sm text-gray-500 dark:text-gray-400">
            💡 <strong>Invited members are always free</strong> — only the
            household creator needs a license.
          </p>
        </div>
      </RevealSection>

      {/* ===FAQ=== */}
      <RevealSection
        id="faq"
        aria-label="Frequently asked questions"
        className="bg-gray-50 py-20 dark:bg-gray-900"
      >
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <p className="mb-2 text-sm font-semibold tracking-widest text-blue-600 uppercase dark:text-blue-400">
              FAQ
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl dark:text-white">
              Frequently asked questions
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div
                key={faq.q}
                className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900"
              >
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white"
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                  aria-expanded={faqOpen === i}
                >
                  <span>{faq.q}</span>
                  <ChevronDown
                    aria-hidden="true"
                    className={`h-4 w-4 shrink-0 text-gray-400 ${
                      faqOpen === i ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {faqOpen === i && (
                  <section
                    aria-label={faq.q}
                    className="border-t border-gray-100 px-6 py-4 text-sm leading-relaxed text-gray-500 dark:border-gray-800 dark:text-gray-400"
                  >
                    {faq.a}
                  </section>
                )}
              </div>
            ))}
          </div>
        </div>
      </RevealSection>

      {/* ===FOOTER=== */}
      <footer className="border-t border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-6 sm:flex-row">
            {/* Hillard Works branding */}
            <div className="flex items-center gap-2.5">
              <a
                href="https://www.hillardworks.com/"
                className="flex items-center gap-3 text-sm font-bold text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 100 100"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M4 10 Q4 4 10 4 L90 4 Q96 4 96 10 L96 63.66 L63.66 96 L10 96 Q4 96 4 90 Z"
                    fill="none"
                    className="stroke-gray-900 dark:stroke-stone-50"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M10 14 Q10 10 14 10 L86 10 Q90 10 90 14 L90 61.32 L61.32 90 L14 90 Q10 90 10 86 Z"
                    className="fill-gray-900 dark:fill-stone-50"
                  />
                </svg>
                <div className="leading-tight">Hillard Works LLC</div>
              </a>
            </div>

            {/* Links */}
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
              <a
                href="https://play.google.com/store/apps/dev?id=8501488979358235639"
                target="_blank"
                rel="noopener noreferrer"
                className="transition hover:text-gray-900 dark:hover:text-white"
                aria-label="Google Play (opens in new tab)"
              >
                Google Play
              </a>
              <Link
                to="/privacy"
                className="transition hover:text-gray-900 dark:hover:text-white"
              >
                Privacy Policy
              </Link>
            </div>
          </div>
          <span className="mt-6 block text-center text-xs text-gray-400 sm:text-left dark:text-gray-600">
            &copy; {new Date().getFullYear()} Hillard Works LLC. All rights
            reserved.
          </span>
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LandingPage — entry point; routes to the appropriate layout by platform
// ---------------------------------------------------------------------------

export default function LandingPage({
  pendingInvitation = null,
}: {
  pendingInvitation?: HouseholdInvitation | null;
}) {
  if (Capacitor.getPlatform() === "android") {
    return <AndroidLandingPage pendingInvitation={pendingInvitation} />;
  }
  return <WebLandingPage pendingInvitation={pendingInvitation} />;
}
